import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { hasAdminAccess } from '../utils/roleUtils';
import { useParams } from 'react-router-dom';
import SignaturePad from '../components/SignaturePad';
import TwoFactorSettings from '../components/TwoFactorSettings';

const positionOptions = [
  'Administrator',
  'Associate Agent',
  'Business Development Manager',
  'Director',
  'Licensed Real Estate Agent',
  'Marketing Manager',
  'Office Manager',
  'Partner',
  'Photographer',
  'Principal',
  'Property Manager',
  'Real Estate Agent',
  'Rental Agent',
  'Rental Assistant',
  'Sales Agent',
  'Sales Assistant',
  'Sales Manager',
  'Styler',
];

const Profile = () => {
  const { user, updateUser, deleteAccount, agency } = useAuth();
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    title: '', // Use title as the source of truth
  });
  const [initialForm, setInitialForm] = useState({ firstName: '', lastName: '', email: '', phone: '', licenseNumber: '', title: '' });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [pendingAvatarBase64, setPendingAvatarBase64] = useState<string | null>(null);
  const [pendingAvatarType, setPendingAvatarType] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [pendingSignatureBase64, setPendingSignatureBase64] = useState<string | null>(null);
  const [pendingSignatureType, setPendingSignatureType] = useState<string | null>(null);
  const [signatureSaving, setSignatureSaving] = useState(false);
  // Fields start read-only; click the edit icon next to each field to toggle editing
  const [editing, setEditing] = useState<{ firstName: boolean; lastName: boolean; phone: boolean; licenseNumber: boolean; title: boolean }>({ firstName: false, lastName: false, phone: false, licenseNumber: false, title: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccessSnackbar, setDeleteSuccessSnackbar] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const isIndividualPlan = ['individual', 'elite individual'].includes(planName);
  const isAdmin = hasAdminAccess(user);

  useEffect(() => {
    // Load fresh profile for this page to avoid relying on login payload
    const loadProfile = async () => {
      try {
        setLoading(true);
        const res = await fetch('/api/auth/profile', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
          }
        });
        if (!res.ok) throw new Error('Failed to load profile');
        const data = await res.json();
        const u = data?.user || user;
        if (u) {
          const newFormData = {
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            email: u.email || '',
            phone: u.phone || '',
            licenseNumber: (u as any).licenseNumber || '',
            title: (u as any).position || '', // Use position field
          };
          setFormData(newFormData);
          setInitialForm(newFormData);
          if (u.avatarImageData && u.avatarImageType) {
            setAvatarPreview(`data:${u.avatarImageType};base64,${u.avatarImageData}`);
          }
          // Load signature if available
          if ((u as any).signatureImageData && (u as any).signatureImageType) {
            setSignaturePreview(`data:${(u as any).signatureImageType};base64,${(u as any).signatureImageData}`);
          }
        }
      } catch (e) {
        console.error('Failed to load profile:', e);
        // Fall back to auth user if available
        if (user) {
          const fallback = {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone || '',
            licenseNumber: (user as any).licenseNumber || '',
            title: (user as any).position || (user as any).title || '', // Use position as primary, fallback to title for backward compatibility
          };
          setFormData(fallback);
          setInitialForm(fallback);
          if ((user as any).avatarImageData && (user as any).avatarImageType) {
            setAvatarPreview(`data:${(user as any).avatarImageType};base64,${(user as any).avatarImageData}`);
          }
          // Load signature if available
          if ((user as any).signatureImageData && (user as any).signatureImageType) {
            setSignaturePreview(`data:${(user as any).signatureImageType};base64,${(user as any).signatureImageData}`);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Submitting form data:', formData);
      // If an avatar change is pending, upload it first
      if (pendingAvatarBase64 && pendingAvatarType) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`/api/auth/${agencySlug}/users/me/avatar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
            body: JSON.stringify({ data: pendingAvatarBase64, type: pendingAvatarType })
          });
          if (!res.ok) throw new Error(await res.text());
        } catch (e) {
          throw new Error('Failed to upload avatar');
        }
      }

      if (!agencySlug) {
        throw new Error('Agency slug is required. Please ensure you are accessing the profile page from the correct URL.');
      }

      const response = await fetch(`/api/auth/${agencySlug}/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          licenseNumber: formData.licenseNumber,
          // Only admins can update position/title; normal users cannot change it themselves
          ...(isAdmin ? { position: formData.title } : {}),
          ...(pendingSignatureBase64 && pendingSignatureType ? {
            signatureImageData: pendingSignatureBase64,
            signatureImageType: pendingSignatureType
          } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update profile' }));
        throw new Error(errorData.message || errorData.error || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      console.log('Received updated user data:', updatedUser);
      if (typeof (updateUser as any) === 'function') {
        (updateUser as any)(updatedUser);
      }
      setSuccess('Profile updated successfully');
      // Clear pending avatar and signature state after successful save
      setPendingAvatarBase64(null);
      setPendingAvatarType(null);
      setPendingSignatureBase64(null);
      setPendingSignatureType(null);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Profile Settings
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{ width: 64, height: 64 }}
                  src={avatarPreview || (user && (user as any).avatarImageData && (user as any).avatarImageType ? `data:${(user as any).avatarImageType};base64,${(user as any).avatarImageData}` : undefined)}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={avatarUploading}
                >
                  {avatarUploading ? 'Uploading…' : 'Change Avatar'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !agencySlug) return;
                      setAvatarUploading(true);
                      setError(null);
                      setSuccess(null);
                      try {
                        // Read file as data URL
                        const dataUrl: string = await new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.onload = () => resolve(reader.result as string);
                          reader.onerror = reject;
                          reader.readAsDataURL(file);
                        });

                        // Resize on client (max 512px) using canvas
                        const img: HTMLImageElement = await new Promise((resolve, reject) => {
                          const image = new Image();
                          image.onload = () => resolve(image);
                          image.onerror = reject;
                          image.src = dataUrl;
                        });

                        const maxSize = 512;
                        let { width, height } = img;
                        if (width > height && width > maxSize) {
                          height = Math.round((maxSize / width) * height);
                          width = maxSize;
                        } else if (height > width && height > maxSize) {
                          width = Math.round((maxSize / height) * width);
                          height = maxSize;
                        } else if (width === height && width > maxSize) {
                          width = height = maxSize;
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) throw new Error('Canvas not supported');
                        ctx.drawImage(img, 0, 0, width, height);

                        // Use JPEG for better compression; fall back to PNG if original is PNG and you prefer lossless
                        const uploadType = 'image/jpeg';
                        const resizedDataUrl = canvas.toDataURL(uploadType, 0.85);

                        // Optimistic preview only; defer upload until Save Changes
                        setAvatarPreview(resizedDataUrl);
                        setPendingAvatarBase64(resizedDataUrl.split(',')[1]);
                        setPendingAvatarType(uploadType);
                      } catch (err: any) {
                        console.error('Avatar update failed:', err);
                        setError('Failed to process avatar');
                      } finally {
                        setAvatarUploading(false);
                      }
                    }}
                  />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Digital Signature
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Required to generate agency agreements.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <SignaturePad
                  width={500}
                  height={200}
                  initialSignature={signaturePreview}
                  saving={signatureSaving}
                  onSave={async (dataUrl) => {
                    console.log('SignaturePad onSave called, dataUrl length:', dataUrl.length);
                    setSignatureSaving(true);
                    setError(null);
                    setSuccess(null);
                    
                    try {
                      // Extract base64 data
                      const base64Data = dataUrl.split(',')[1];
                      if (!base64Data) {
                        console.error('Failed to extract base64 data from dataUrl');
                        setError('Failed to process signature');
                        setSignatureSaving(false);
                        return;
                      }

                      // Immediately save to profile via API
                      const token = localStorage.getItem('token');
                      const response = await fetch(`/api/auth/${agencySlug}/users/me`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          signatureImageData: base64Data,
                          signatureImageType: 'image/png',
                        }),
                      });

                      if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Failed to save signature');
                      }

                      const updatedUser = await response.json();
                      console.log('Signature saved successfully:', updatedUser);
                      
                      // Update local state
                      setSignaturePreview(dataUrl);
                      // Clear pending state since signature is already saved
                      setPendingSignatureBase64(null);
                      setPendingSignatureType(null);
                      
                      // Update user context if available
                      if (typeof (updateUser as any) === 'function') {
                        (updateUser as any)(updatedUser);
                      }
                      
                      setSuccess('Signature saved successfully!');
                      console.log('Signature state updated successfully');
                    } catch (err) {
                      console.error('Error in onSave callback:', err);
                      setError(err instanceof Error ? err.message : 'Failed to save signature');
                    } finally {
                      setSignatureSaving(false);
                    }
                  }}
                  onClear={() => {
                    setSignaturePreview(null);
                    setPendingSignatureBase64(null);
                    setPendingSignatureType(null);
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
                Draw your signature above. Your signature will appear at the end of consultation reports.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={!editing.firstName}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setEditing(prev => ({ ...prev, firstName: !prev.firstName }))}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={!editing.lastName}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setEditing(prev => ({ ...prev, lastName: !prev.lastName }))}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                helperText="Enter your phone number with country code (e.g., +61 400 000 000)"
                required
                disabled={!editing.phone}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setEditing(prev => ({ ...prev, phone: !prev.phone }))}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="License Number"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                placeholder={!formData.licenseNumber ? 'Enter your real estate license number' : undefined}
                helperText="Required to generate agency agreements"
                disabled={!editing.licenseNumber}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setEditing(prev => ({ ...prev, licenseNumber: !prev.licenseNumber }))}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={positionOptions}
                disabled={!isAdmin || !editing.title}
                value={formData.title}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, title: newValue || '' }));
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData(prev => ({ ...prev, title: newInputValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Position"
                    placeholder="e.g. Sales Agent, Principal, Licensed Agent"
                    helperText="Your position will be displayed on agency agreements and other legal documents"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {params.InputProps.endAdornment}
                          {isAdmin && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setEditing(prev => ({ ...prev, title: !prev.title }))}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )}
                        </Box>
                      )
                    }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiAutocomplete-paper': {
                        minWidth: '350px !important',
                        width: 'auto !important',
                      },
                      '& .MuiAutocomplete-listbox': {
                        minWidth: '350px',
                        width: '100%',
                      },
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'clip',
                        padding: '8px 16px',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || (
                  formData.firstName === initialForm.firstName &&
                  formData.lastName === initialForm.lastName &&
                  formData.phone === initialForm.phone &&
                  formData.licenseNumber === initialForm.licenseNumber &&
                  formData.title === initialForm.title &&
                  !pendingAvatarBase64 &&
                  !pendingSignatureBase64
                )}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </form>

        <TwoFactorSettings />

        {/* Delete Account - shown for individual plan (no Settings) and optionally for all */}
        {isIndividualPlan && (
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>Account Management</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deleting your account will permanently remove all your data and cannot be undone.
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Account'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Delete Account Confirmation Dialogs */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" /> Delete Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.
          </Typography>
          <Alert severity="warning">This will remove your account and all associated data from the system.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmDialogOpen(true); setDeleteConfirmationText(''); setDeleteAcknowledged(false); }} variant="contained" color="error" startIcon={<DeleteIcon />}>Continue</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmDialogOpen} onClose={() => !deleting && setDeleteConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will permanently delete your account and all associated data. Are you absolutely sure?
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>This action cannot be undone. All your data will be permanently removed.</Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              To confirm, type <strong>DELETE</strong> below:
            </Typography>
            <TextField
              fullWidth
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="Type DELETE to confirm"
              disabled={deleting}
              error={deleteConfirmationText !== '' && deleteConfirmationText !== 'DELETE'}
              helperText={deleteConfirmationText !== '' && deleteConfirmationText !== 'DELETE' ? 'Must match exactly: DELETE' : ''}
            />
          </Box>
          <FormControlLabel
            control={<Checkbox checked={deleteAcknowledged} onChange={(e) => setDeleteAcknowledged(e.target.checked)} disabled={deleting} />}
            label={<Typography variant="body2">I understand this is permanent and cannot be undone.</Typography>}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmDialogOpen(false); setDeleteConfirmationText(''); setDeleteAcknowledged(false); }} variant="outlined" disabled={deleting}>Cancel</Button>
          <Button
            onClick={async () => {
              setDeleteConfirmDialogOpen(false);
              setDeleting(true);
              setError(null);
              try {
                await deleteAccount();
                setDeleteSuccessSnackbar(true);
              } catch (err: any) {
                setDeleting(false);
                setError(err?.response?.data?.error || err?.message || 'Failed to delete account.');
              }
            }}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            disabled={deleting || deleteConfirmationText !== 'DELETE' || !deleteAcknowledged}
          >
            {deleting ? 'Deleting...' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={deleteSuccessSnackbar} autoHideDuration={3000} onClose={() => setDeleteSuccessSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setDeleteSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }}>Your account has been successfully deleted.</Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 